import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, FileText, FileSpreadsheet, Upload, Loader2, Calendar,
} from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { fmtCompact, fmtNum } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SintesiTab } from "@/components/tabs/SintesiTab";
import { IndiciTab } from "@/components/tabs/IndiciTab";
import { RendicontoTab } from "@/components/tabs/RendicontoTab";
import { ConfrontoTab } from "@/components/tabs/ConfrontoTab";
import { DscrTab } from "@/components/tabs/DscrTab";
import { ActivityTab } from "@/components/tabs/ActivityTab";
import { ManualEntryDialog } from "@/components/ManualEntryDialog";
import { NoteDialog } from "@/components/NoteDialog";
import { ExportDialog } from "@/components/ExportDialog";
import { SectionNote } from "@/components/SectionNote";
import { PencilRuler, StickyNote } from "lucide-react";

export default function Company() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [year, setYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sectors, setSectors] = useState([]);
  const fileRef = useRef(null);

  const loadCompany = () => api.get(`/companies/${id}`).then((r) => {
    setCompany(r.data);
    if (r.data.years?.length) setYear((y) => y || r.data.years[0]);
    return r.data;
  });

  const loadAnalysis = () => {
    if (!year) return Promise.resolve();
    return api.get(`/companies/${id}/analysis`, { params: { year } })
      .then((r) => setAnalysis(r.data))
      .catch(() => setAnalysis(null));
  };

  const changeSector = async (v) => {
    try {
      await api.put(`/companies/${id}/profile`, { sector_profile: v });
      setCompany((c) => ({ ...c, sector_profile: v }));
      await loadAnalysis();
      toast.success("Settore aggiornato: benchmark e giudizio ricalcolati");
    } catch (e) {
      toast.error("Errore nell'aggiornamento del settore");
    }
  };

  const updateNote = (section, text) =>
    setCompany((c) => ({ ...c, notes: { ...(c?.notes || {}), [section]: text } }));

  useEffect(() => {
    api.get("/sectors").then((r) => setSectors(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    loadCompany().catch(() => toast.error("Azienda non trovata")).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    loadAnalysis();
  }, [id, year]);

  const doExport = async (fmt, sections, exYear, order, extra = {}) => {
    setExporting(fmt);
    try {
      const token = localStorage.getItem("bcee_token");
      const p = {
        riclass: !!sections.riclass, indici: !!sections.indici, confronto: !!sections.confronto,
        rendiconto: !!sections.rendiconto, dscr_sec: !!sections.dscr, note: !!sections.note,
        cover: !!sections.cover,
        panoramica: sections.panoramica !== false,
      };
      if (exYear) p.year = exYear;
      if (order && order.length) p.section_order = order.join(",");
      if (extra.lang) p.lang = extra.lang;
      if (extra.office) p.office = extra.office;
      const qp = new URLSearchParams(p).toString();
      const res = await fetch(`${API}/companies/${id}/export/${fmt}?${qp}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Report_${company?.name?.trim().replace(/\s+/g, "_") || "azienda"}.${fmt === "excel" ? "xlsx" : fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Report ${fmt.toUpperCase()} scaricato`);
    } catch (e) {
      toast.error("Errore durante l'esportazione");
    } finally {
      setExporting(null);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/upload-xbrl?company_id=${id}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Bilancio aggiunto: esercizi ${data.years.join(", ")}`);
      const c = await loadCompany();
      setYear(c.years[0]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore caricamento");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...</div></Layout>;
  }
  if (!company) return null;

  const k = company.kpi || {};
  const hasData = company.years?.length > 0;

  return (
    <Layout>
      <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="mb-4 -ml-2" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Torna alle aziende
      </Button>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-4">
          <div className="grid place-items-center h-12 w-12 rounded-lg bg-primary/10 text-primary shrink-0">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{company.name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground font-mono">
              <span>{company.sector || "—"}</span>
              {company.codice_fiscale && <span>CF/P.IVA: {company.codice_fiscale}</span>}
              <Badge variant="outline" className="text-[10px]">{company.source === "xbrl" ? "Da XBRL" : company.source === "sample" ? "Esempio" : "Manuale"}</Badge>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".xbrl,.xml,.zip" className="hidden" onChange={handleUpload} data-testid="company-xbrl-input" />
          <ManualEntryDialog
            companyId={id}
            years={company.years}
            defaultYear={year || new Date().getFullYear()}
            onSaved={async (savedYear) => { const c = await loadCompany(); setYear(savedYear); }}
            trigger={<Button variant="outline" size="sm" data-testid="manual-entry-btn"><PencilRuler className="h-4 w-4 mr-1.5" /> Voci Manuali</Button>}
          />
          <NoteDialog
            companyId={id}
            initialNote={company.report_note}
            onSaved={(n) => setCompany((c) => ({ ...c, report_note: n }))}
            trigger={<Button variant="outline" size="sm" data-testid="note-btn"><StickyNote className="h-4 w-4 mr-1.5" /> Note Report</Button>}
          />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="company-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Carica XBRL
          </Button>
          <ExportDialog
            exporting={exporting}
            onExport={doExport}
            years={company.years}
            defaultYear={year}
            trigger={<Button variant="outline" size="sm" disabled={!hasData || !!exporting} data-testid="export-btn"><FileText className="h-4 w-4 mr-1.5" /> Esporta Report</Button>}
          />
        </div>
      </div>

      {!hasData ? (
        <Card className="p-12 text-center border-dashed">
          <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading font-semibold text-lg">Nessun bilancio caricato</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Carica un file XBRL per generare indici, rendiconto e report.</p>
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="empty-upload-btn">
            <Upload className="h-4 w-4 mr-2" /> Carica XBRL
          </Button>
        </Card>
      ) : (
        <>
          {/* KPI banner */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6">
            {[
              ["Fatturato", fmtCompact(k.fatturato)],
              ["Valore Produzione", fmtCompact(k.valore_produzione)],
              ["EBITDA", fmtCompact(k.ebitda)],
              ["EBITDA Margin", fmtNum(k.ebitda_margin, 1) + "%"],
              ["Utile Netto", fmtCompact(k.utile)],
              ["PFN", fmtCompact(k.pfn)],
            ].map(([label, val]) => (
              <Card key={label} className="p-4" data-testid={`kpi-${label}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                <div className="font-mono font-bold text-lg lg:text-xl mt-1 tracking-tight">{val}</div>
              </Card>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Esercizio di riferimento:</span>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-32 font-mono" data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {company.years.map((y) => (
                  <SelectItem key={y} value={String(y)} data-testid={`year-option-${y}`}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground ml-2">Settore (benchmark):</span>
            <Select value={company.sector_profile || "generico"} onValueChange={changeSector}>
              <SelectTrigger className="w-60" data-testid="sector-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sectors.map((s) => (
                  <SelectItem key={s.key} value={s.key} data-testid={`sector-option-${s.key}`}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Tabs defaultValue="sintesi" className="w-full">
            <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 mb-6" data-testid="analysis-tabs">
              <TabsTrigger value="sintesi" data-testid="tab-sintesi">Riclassificazioni</TabsTrigger>
              <TabsTrigger value="indici" data-testid="tab-indici">Indici</TabsTrigger>
              <TabsTrigger value="rendiconto" data-testid="tab-rendiconto">Rendiconto</TabsTrigger>
              <TabsTrigger value="confronto" data-testid="tab-confronto">Confronto</TabsTrigger>
              <TabsTrigger value="dscr" data-testid="tab-dscr">DSCR Prosp.</TabsTrigger>
              <TabsTrigger value="registro" data-testid="tab-registro">Registro</TabsTrigger>
            </TabsList>
            <TabsContent value="sintesi"><SintesiTab analysis={analysis} /></TabsContent>
            <TabsContent value="indici">
              <IndiciTab analysis={analysis} companyId={id} year={year} onOverrideSaved={loadAnalysis} />
              <SectionNote companyId={id} section="indici" label="Commento agli Indici di Bilancio" value={company.notes?.indici} onSaved={updateNote} />
            </TabsContent>
            <TabsContent value="rendiconto">
              <RendicontoTab companyId={id} year={year} />
              <SectionNote companyId={id} section="rendiconto" label="Commento al Rendiconto Finanziario" value={company.notes?.rendiconto} onSaved={updateNote} />
            </TabsContent>
            <TabsContent value="confronto"><ConfrontoTab companyId={id} /></TabsContent>
            <TabsContent value="dscr">
              <DscrTab companyId={id} />
              <SectionNote companyId={id} section="dscr" label="Commento al DSCR Prospettico" value={company.notes?.dscr} onSaved={updateNote} />
            </TabsContent>
            <TabsContent value="registro"><ActivityTab companyId={id} /></TabsContent>
          </Tabs>

          <SectionNote companyId={id} section="generale" label="Note Generali dell'Analista (in fondo al report)" value={company.notes?.generale} onSaved={updateNote} />
        </>
      )}
    </Layout>
  );
}
